"use client";
import React from "react";
import { Suspense } from "react";
import styles from "../getting-the-basis-of-advice-right-page/getting-the-basis-of-advice-right-page.module.css";
import "../../../src/common/grid.css";
import BlogNavigation from "../../../components/BlogNavigation/BlogNavigation";
import BlogSearchSection from "../../../components/BlogSearchSection/BlogSearchSection";
import Disclaimer from "../../../components/BlogDiclaimerComponent/BlogDisclaimerComponent";
import Link from "next/link";
import BlogCardSlider from "../../../components/BlogCardSlider/BlogCardSlider";
import { useTranslation } from "react-i18next";
import BlogSpreadLoveComponent from "../../../components/BlogSpreadLoveComponent/BlogSpreadLoveComponent";
import BlogStartSection from "../../../components/BlogStartSection/BlogStartSection";
import BlogLeaveReplyComponent from "../../../components/BlogLeaveReplyComponent/BlogLeaveReplyComponent";
import {usePosts} from "../../../src/app/blogs/posts.js";
import pexels from "../../../src/assets/images/pexels.webp";

export default function WhatIsAssetFinance() {
    const { t } = useTranslation();
    const posts = usePosts();
    // Blog metadata
    const blogData = {
        title: t("What is Asset Finance? Everything You Need to Know"),
        author: "Matthew Clark",
        category: "Insights",
        relatedCategory: "insights",
        displayCategory: t("Insights"),
        categorySlug: "insights",
        image: pexels,
        url: "/blogs/what-is-asset-finance",
    };

    // Filter related posts (only posts with the same relatedCategory, excluding current blog)
    const relatedPosts = posts
        .filter(
            (post) => post.relatedCategory === blogData.relatedCategory && post.slug !== blogData.url
        )
        .slice(0, 10);

    return (
        <>
            <div className={styles.blogWrapper}>
                <div className={`${styles.flexContainer} container`}>
                    <div className="row">
                        <div className="col-12 col-md-8 col-lg-8">
                            <div className={styles.blogContentWrapper}>
                                <BlogNavigation
                                    items={[
                                        { title: t("Key Choice Lending"), path: "/" },
                                        { title: t("Blogs"), path: "/blogs" },
                                        {
                                            title: blogData.displayCategory,
                                            path: `/blogs/category/${blogData.categorySlug}`,
                                        },
                                    ]}
                                    currentPageTitle={blogData.title}
                                />

                                <BlogStartSection
                                    title={blogData.title}
                                    author={blogData.author}
                                    category={blogData.relatedCategory}
                                    image={blogData.image}
                                    alt={t("what-is-asset-finance-image")}
                                />

                                <BlogSpreadLoveComponent url={blogData.url} title={blogData.title} />

                                <article className={styles.blogContent}>
                                    <h1 className={styles.big}>{t("What Is Asset Finance?")}</h1>

                                    <p className={styles.small}>
                                        {t(
                                            "Purchasing a business asset is often decided upon with a cost-benefit analysis. Further complexities are added when businesses look to make these purchases with an eye on impending tax events."
                                        )}
                                    </p>
                                    <p className={styles.small}>
                                        {t(
                                            "As the government budget has recently made depreciation made less generous, that decision becomes more time sensitive. Temporary full expensing (TFE) will cease and be replaced by a $20,000 instant asset write-off (IAWO) from 1 July 2023."
                                        )}
                                    </p>
                                    <p className={styles.small}>
                                        {t(
                                            "Under this change, small businesses (aggregated annual turnover of less than $10 million) will be able to immediately deduct the full cost of eligible assets costing less than $20,000 that is first used or installed ready for use between 1 July 2023 and 30 June 2024."
                                        )}
                                    </p>
                                    <p className={styles.small}>
                                        {t(
                                            "Assets valued at $20,000 or more (which cannot be immediately deducted) will be placed into a small business simplified depreciation pool and depreciated at 15% in the first income year and 30% each income year thereafter."
                                        )}
                                    </p>
                                    <p className={styles.small}>
                                        {t(
                                            "For larger businesses, the write-off threshold is cut to $1,000. TFE, which allows eligible businesses with a turnover of less than $5 billion to deduct the full cost of eligible depreciable assets of any value is however still available up to 30 June 2023. What this means: This may impact your business’s cash flow when acquiring assets above the relevant thresholds as your depreciation deductions will not be claimable upfront."
                                        )}
                                    </p>

                                    <h1 className={styles.big}>{t("What is Equipment Finance?")}</h1>

                                    <p className={styles.small}>
                                        {t(
                                            "Equipment finance refers to the various financial instruments that businesses and individuals use to purchase or lease equipment. Here are some of the different types of equipment finance:"
                                        )}
                                    </p>
                                    <h2
                                        className={styles.pointHeading}
                                        style={{ color: "#333", fontSize: "1.5rem" }}
                                    >
                                        {t("Equipment Loans:") || "Equipment Loans:"}
                                    </h2>

                                    <p className={styles.small}>
                                        {t(
                                            "Equipment loans are a type of financing that allows businesses to purchase equipment with a lump sum payment and pay it back over a fixed period of time with interest."
                                        )}
                                    </p>
                                    <h2
                                        className={styles.pointHeading}
                                        style={{ color: "#333", fontSize: "1.5rem" }}
                                    >
                                        {t("Equipment Leases:") || "Equipment Leases:"}
                                    </h2>

                                    <p className={styles.small}>
                                        {t(
                                            "Equipment leasing allows businesses to rent equipment for a period of time and return it when the lease term ends. The lease may include an option to buy the equipment at the end of the lease term."
                                        )}
                                    </p>
                                    <h2
                                        className={styles.pointHeading}
                                        style={{ color: "#333", fontSize: "1.5rem" }}
                                    >
                                        {t("Sale and Leaseback:") || "Sale and Leaseback:"}
                                    </h2>

                                    <p className={styles.small}>
                                        {t(
                                            "This financing option allows businesses to sell their owned equipment to a leasing company, which then leases it back to them. This option provides businesses with cash upfront while still allowing them to use the equipment."
                                        )}
                                    </p>
                                    <h2
                                        className={styles.pointHeading}
                                        style={{ color: "#333", fontSize: "1.5rem" }}
                                    >
                                        {t("Equipment Rental:") || "Equipment Rental:"}
                                    </h2>

                                    <p className={styles.small}>
                                        {t(
                                            "Equipment rental is a short-term option that allows businesses to rent equipment for a specific project or period of time."
                                        )}
                                    </p>
                                    <h2
                                        className={styles.pointHeading}
                                        style={{ color: "#333", fontSize: "1.5rem" }}
                                    >
                                        {t("Equipment Line of Credit:") || "Equipment Line of Credit:"}
                                    </h2>

                                    <p className={styles.small}>
                                        {t(
                                            "This type of financing provides businesses with a line of credit that they can use to purchase or lease equipment as needed."
                                        )}
                                    </p>
                                    <h2
                                        className={styles.pointHeading}
                                        style={{ color: "#333", fontSize: "1.5rem" }}
                                    >
                                        {t("Asset-Based Lending:") || "Asset-Based Lending:"}
                                    </h2>

                                    <p className={styles.small}>
                                        {t(
                                            "Asset-based lending is a financing option that uses a company and assets, such as equipment, as collateral for a loan."
                                        )}
                                    </p>

                                    <section className={styles.contentSection}>
                                        <h1 className={styles.big}>
                                            {t("What Equipment Finance is Best for Me?")}
                                        </h1>

                                        <p className={styles.small}>
                                            {t(
                                                "Overall, the type of equipment finance that is best for a business will depend on their unique financial situation, cash flow needs, and the type of equipment they need to purchase or lease."
                                            )}
                                        </p>
                                        <p className={styles.small}>
                                            {t(
                                                "The best time to finance an asset depends on various factors, including the type of asset, current market conditions, interest rates, and your financial situation. However, there are a few general considerations to keep in mind when deciding when to finance an asset:"
                                            )}
                                        </p>

                                        <h2
                                            className={styles.pointHeading}
                                            style={{ color: "#333", fontSize: "1.5rem" }}
                                        >
                                            {t("Interest rates:") || "Interest rates:"}
                                        </h2>
                                        <p className={styles.small}>
                                            {t(
                                                "When interest rates are low, it can be an excellent time to finance an asset because you’ll pay less in interest over the life of the loan."
                                            )}
                                        </p>
                                        <h2
                                            className={styles.pointHeading}
                                            style={{ color: "#333", fontSize: "1.5rem" }}
                                        >
                                            {t("Cash flow:") || "Cash flow:"}
                                        </h2>
                                        <p className={styles.small}>
                                            {t(
                                                "If you don’t have enough cash on hand to purchase the asset outright, financing can help you acquire the asset and spread the cost over time."
                                            )}
                                        </p>
                                        <h2
                                            className={styles.pointHeading}
                                            style={{ color: "#333", fontSize: "1.5rem" }}
                                        >
                                            {t("Depreciation:") || "Depreciation:"}
                                        </h2>
                                        <p className={styles.small}>
                                            {t(
                                                "If the asset depreciates quickly, it may be better to finance it rather than pay for it in cash upfront, as you’ll avoid tying up too much of your capital."
                                            )}
                                        </p>
                                        <h2
                                            className={styles.pointHeading}
                                            style={{ color: "#333", fontSize: "1.5rem" }}
                                        >
                                            {t("Investment return:") || "Investment return:"}
                                        </h2>
                                        <p className={styles.small}>
                                            {t(
                                                "If the asset is expected to generate a higher return than the cost of financing, it may make sense to finance it. Timing: Consider the timing of your purchase. If you need the asset right away and don’t have the cash to pay for it upfront, financing may be the best option."
                                            )}
                                        </p>
                                        <p className={styles.small}>
                                            {t(
                                                "In general, it’s important to carefully evaluate your financial situation, the asset, and the current market conditions before deciding when to finance an asset. Consulting with a financial advisor or accountant can also be helpful in making an informed decision and of course, we are here to help with your bespoke financing needs."
                                            )}
                                        </p>
                                        <p className={styles.small}>
                                            {t(
                                                "Key Choice Lending – Experts in helping finance the people and businesses that make Australia Great."
                                            )}{" "}
                                        </p>
                                        <p className={styles.small}>
                                            {t(
                                                "Your full financial needs and requirements need to be assessed prior to any offer or acceptance of a loan product. Credit Representative 508009 is authorised under the Australian Credit Licence 538623."
                                            )}
                                        </p>
                                    </section>
                                    <div className={styles.meetingDiv}>
                                        <Link
                                            href="/booking"
                                            className={styles.ctaButton}
                                            style={{ fontWeight: "bold" }}
                                        >
                                            {t("Book a Meeting Here")}
                                        </Link>
                                    </div>
                                    <Disclaimer />
                                </article>
                                <BlogSpreadLoveComponent url={blogData.url} title={blogData.title} />
                            </div>
                        </div>
                        <div className="col-12 col-md-4 col-lg-4">
                            <div className={styles.searchSection}>
                                <Suspense>
                                    <BlogSearchSection />
                                </Suspense>
                            </div>
                        </div>
                    </div>
                </div>
                <div className={styles.BlogCardSliderWrapper}>
                    <div className="container">
                        <div className={styles.BlogCardSliderContent}>
                            <BlogCardSlider posts={relatedPosts} />
                        </div>
                    </div>
                </div>
                <div className={styles.leaveReplyWrapper}>
                    <div className="container">
                        <div className={styles.leaveReplyContent}>
                            <BlogLeaveReplyComponent />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}